import React, { useRef, useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Button, Spinner } from "react-bootstrap";
import { getToken, getUserId } from '../services/authorize';
import Swal from 'sweetalert2';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import { useParams } from "react-router-dom";
import { convertGender } from '../utils/studentUtils';
import "./PrintPP.css";
import "./../print.css";
import useSchoolSettings from '../hooks/useSchoolSettings';

const PARENT_STATUS = { togather: 'อยู่ด้วยกัน', separated: 'แยกกันอยู่', divorce: 'หย่าร้าง' };

const HP_MONTHS = [
  { label: 'มิถุนายน',   num: 6  },
  { label: 'กรกฎาคม',    num: 7  },
  { label: 'พฤศจิกายน',  num: 11 },
  { label: 'ธันวาคม',    num: 12 },
];

const joinAddress = (...parts) => parts.filter(Boolean).join(' ') || null;

const calcBMI = (height, weight) => {
  const h = parseFloat(height);
  const w = parseFloat(weight);
  if (!h || !w || h <= 0) return null;
  return w / ((h / 100) ** 2);
};

const bmiText = (bmi) => {
  if (bmi === null) return '';
  const label = bmi < 18.5 ? 'น้ำหนักน้อย' : bmi < 23 ? 'ปกติ' : bmi < 27.5 ? 'น้ำหนักเกิน' : 'อ้วน';
  return `${bmi.toFixed(1)} (${label})`;
};

const filterYears = (history, isJunior) =>
  [...new Map(
    history
      .filter(r => isJunior ? Number(r.c_id) <= 3 : Number(r.c_id) > 3)
      .map(r => [r.year_id, r])
  ).values()].slice(0, 3);

// Replace input elements with their current values for clean print output
const printNode = (node) => {
  if (!node) return;
  const clone = node.cloneNode(true);
  clone.querySelectorAll('input').forEach(inp => {
    const span = document.createElement('span');
    span.textContent = inp.value || '';
    inp.replaceWith(span);
  });
  const w = window.open('', '_blank');
  w.document.write(`
    <html><head><title>ปพ.8</title>
    <style>
      @page { size: A4; margin: 0; }
      body { margin: 0; font-family: 'Cordia New','Tahoma',Arial,sans-serif; font-size: 14pt; }
      table { border-collapse: collapse; width: 100%; }
      td, th { border: 1px solid #000; padding: 2px 6px; vertical-align: middle; }
      .a4-print { width: 210mm; min-height: 297mm; padding: 14mm 16mm; box-sizing: border-box; }
      .no-print { display: none !important; }
      .print-only { display: inline !important; }
    </style>
    </head><body>
    <div class="a4-print">${clone.innerHTML}</div>
    </body></html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 400);
};

const UL = ({ v, w, block = false }) => (
  <span style={{
    display: block ? 'block' : 'inline-block',
    borderBottom: '1px solid #000',
    width: block ? '100%' : undefined,
    minWidth: block ? undefined : (w || 120),
    paddingBottom: 1,
    marginLeft: block ? 0 : 2,
    marginRight: block ? 0 : 4,
    textAlign: 'center',
  }}>{v || ' '}</span>
);

// --- shared module-level helpers ---
const ppSec = (label) => (
  <div style={{
    fontWeight: 'bold', fontSize: '12.5pt',
    borderBottom: '1.5px solid #777',
    paddingBottom: 2, marginTop: 14, marginBottom: 8,
    color: '#333',
  }}>{label}</div>
);

const ppRow = (children, extra = {}) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 8, ...extra }}>
    {children}
  </div>
);

const ppLb = (text) => (
  <span style={{ whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.6 }}>{text}</span>
);

const ppVl = (val, flex = 1, minW = 30) => (
  <span style={{
    flex, minWidth: minW,
    borderBottom: '1px solid #000',
    textAlign: 'center', paddingBottom: 1,
    display: 'inline-block',
  }}>{val || ' '}</span>
);

const hpField = (val) => (
  <div style={{
    borderBottom: '1px solid #000',
    minHeight: 24, marginBottom: 10,
    paddingBottom: 2, paddingLeft: 4,
    textAlign: 'left',
    textIndent: val ? '2em' : undefined,
  }}>{val || ' '}</div>
);

function CoverPagePP8({ student, studyHistory, school }) {
  const ref = useRef();
  const { school_name, school_address, school_office, logoSrc } = school || {};
  const firstEntry = studyHistory?.[0];
  const firstYear  = firstEntry?.year_year || '';
  const level = firstEntry
    ? (Number(firstEntry.c_id) <= 3 ? 'ตอนต้น' : 'ตอนปลาย')
    : '';

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref}
        style={{ fontSize: '14pt', display: 'flex', flexDirection: 'column',
                 alignItems: 'center', justifyContent: 'space-between', minHeight: '267mm' }}>

        <div style={{ textAlign: 'center', width: '100%' }}>
          <img src={logoSrc} alt="logo" style={{ width: 100, height: 100, marginBottom: 10 }} />
          <div style={{ fontSize: '24pt', fontWeight: 'bold', marginBottom: 60 }}>
            {'ระเบียนสะสม ปพ.8'}
          </div>
          <div style={{ margin: '0 auto 50px', border: '1px solid #000', width: 100, height: 130,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', fontSize: '12pt', gap: 6 }}>
            <div>{'ติดรูป'}</div>
            <div>{'นักเรียน'}</div>
          </div>
          <div style={{ display: 'inline-block', textAlign: 'left', fontSize: '14pt' }}>
            <div style={{ marginBottom: 14 }}>
              {'ชื่อ-สกุล'}{' '}
              <UL v={student?.st_name} w={300} />
              {'เลขประจำตัว'}{' '}
              <UL v={student?.st_idnumber} w={100} />
            </div>
            <div>
              {'ระดับชั้นมัธยมศึกษา '}{level}
              {'    ปีการศึกษาที่เข้าเรียน '}
              <UL v={firstYear} w={120} />
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: '14pt', paddingBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{school_name}</div>
          <div style={{ marginBottom: 4 }}>{school_address}</div>
          <div>{school_office}</div>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant="primary" className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i>{' ปริ้นหน้าปก'}
        </Button>
      </div>
    </div>
  );
}

function PersonalPagePP8({ student, onSaved }) {
  const ref = useRef();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (student) {
      const next = { ...student };
      if (!next.st_averageAnnualIncome) {
        const father = parseFloat(next.st_fatherAnnualIncome) || 0;
        const mother = parseFloat(next.st_motherAnnualIncome) || 0;
        if (father || mother) next.st_averageAnnualIncome = String(father + mother);
      }
      setForm(next);
    }
  }, [student]);

  const sf = (field, value) => setForm(f => {
    const next = { ...f, [field]: value };
    if ((field === 'st_fatherAnnualIncome' || field === 'st_motherAnnualIncome') && !f.st_averageAnnualIncome) {
      const father = parseFloat(field === 'st_fatherAnnualIncome' ? value : f.st_fatherAnnualIncome) || 0;
      const mother = parseFloat(field === 'st_motherAnnualIncome' ? value : f.st_motherAnnualIncome) || 0;
      if (father || mother) next.st_averageAnnualIncome = String(father + mother);
    }
    return next;
  });

  const inStyle = { width: '100%', border: 'none', outline: 'none', background: 'transparent', textAlign: 'center', fontSize: 'inherit', padding: 0 };
  const vlStyle = (flex, minW) => ({ flex, minWidth: minW || 30, borderBottom: '1px solid #000', textAlign: 'center', paddingBottom: 1, display: 'inline-block' });

  const pi = (field, flex = 1, minW = 30) => (
    <span style={vlStyle(flex, minW)}>
      <span className="no-print">
        <input type="text" value={form[field] ?? ''} onChange={e => sf(field, e.target.value)} style={inStyle} />
      </span>
      <span className="print-only">{form[field] || ' '}</span>
    </span>
  );

  const ps = (field, options, flex = 1) => (
    <span style={vlStyle(flex, 60)}>
      <span className="no-print">
        <select value={form[field] ?? ''} onChange={e => sf(field, e.target.value)}
          style={{ ...inStyle, cursor: 'pointer' }}>
          <option value="">-</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </span>
      <span className="print-only">{options.find(o => o.value === form[field])?.label || form[field] || ' '}</span>
    </span>
  );

  const piIdCard = (flex = 1, minW = 30) => {
    const val = form.st_idcard ?? '';
    const invalid = val.length > 0 && (val.length !== 13 || !/^\d{13}$/.test(val));
    return (
      <span style={{ ...vlStyle(flex, minW), outline: invalid ? '1px solid #dc3545' : undefined }}>
        <span className="no-print">
          <input type="text" value={val} maxLength={13}
            onChange={e => sf('st_idcard', e.target.value.replace(/\D/g, '').slice(0, 13))}
            style={{ ...inStyle, color: invalid ? '#dc3545' : undefined }}
            placeholder="0000000000000" />
        </span>
        <span className="print-only">{val || ' '}</span>
      </span>
    );
  };

  const piDate = (field, flex = 1, minW = 80) => {
    const val = form[field] ?? '';
    const invalid = val.length > 0 && !/^\d{2}\/\d{2}\/\d{4}$/.test(val);
    const handleChange = (raw) => {
      const digits = raw.replace(/\D/g, '').slice(0, 8);
      let fmt = digits;
      if (digits.length > 4) fmt = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
      else if (digits.length > 2) fmt = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      sf(field, fmt);
    };
    return (
      <span style={{ ...vlStyle(flex, minW), outline: invalid ? '1px solid #dc3545' : undefined }}>
        <span className="no-print">
          <input type="text" value={val} maxLength={10}
            onChange={e => handleChange(e.target.value)}
            style={{ ...inStyle, color: invalid ? '#dc3545' : undefined }}
            placeholder="วว/ดด/ปปปป" />
        </span>
        <span className="print-only">{val || ' '}</span>
      </span>
    );
  };

  const addrRow = (label, addrField, subField, distField, provField) => ppRow(<>
    {ppLb(label)}
    <span style={{ flex: 1 }}>
      <span className="no-print" style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ flex: 3, borderBottom: '1px solid #000' }}>
          <input value={form[addrField] ?? ''} onChange={e => sf(addrField, e.target.value)} style={{ ...inStyle, width: '100%' }} />
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>ตำบล</span>
        <span style={{ flex: 1.2, borderBottom: '1px solid #000' }}>
          <input value={form[subField] ?? ''} onChange={e => sf(subField, e.target.value)} style={{ ...inStyle, width: '100%' }} />
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>อำเภอ</span>
        <span style={{ flex: 1.2, borderBottom: '1px solid #000' }}>
          <input value={form[distField] ?? ''} onChange={e => sf(distField, e.target.value)} style={{ ...inStyle, width: '100%' }} />
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>จังหวัด</span>
        <span style={{ flex: 1.2, borderBottom: '1px solid #000' }}>
          <input value={form[provField] ?? ''} onChange={e => sf(provField, e.target.value)} style={{ ...inStyle, width: '100%' }} />
        </span>
      </span>
      <span className="print-only">
        <span style={{ display: 'block', borderBottom: '1px solid #000', paddingBottom: 1, width: '100%', textAlign: 'center' }}>
          {form[addrField] || ' '}&nbsp;&nbsp;ตำบล&nbsp;&nbsp;{form[subField] || ' '}&nbsp;&nbsp;อำเภอ&nbsp;&nbsp;{form[distField] || ' '}&nbsp;&nbsp;จังหวัด&nbsp;&nbsp;{form[provField] || ' '}
        </span>
      </span>
    </span>
  </>);

  const handleSave = () => {
    setSaving(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/updateStudentInfo`, form,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
      if (onSaved) onSaved(form);
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' });
    }).finally(() => setSaving(false));
  };

  const GENDER_OPT = [{ value: 'm', label: 'ชาย' }, { value: 'f', label: 'หญิง' }];
  const PSTATUS_OPT = [
    { value: 'togather',  label: 'อยู่ด้วยกัน' },
    { value: 'separated', label: 'แยกกันอยู่' },
    { value: 'divorce',   label: 'หย่าร้าง' },
  ];

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '12.5pt', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 'bold', fontSize: '15pt', marginBottom: 6 }}>{'1. ข้อมูลส่วนตัว'}</div>

        {ppSec('ข้อมูลพื้นฐาน')}
        {ppRow(<>
          {ppLb('รหัสประจำตัว')}
          <span style={{ flex: 2, minWidth: 30, borderBottom: '1px solid #000', textAlign: 'center', paddingBottom: 1, display: 'inline-block', background: '#f5f5f5' }}>
            {form.st_idnumber || ' '}
          </span>
          {ppLb('วันเดือนปีเกิด')}
          {piDate('st_birthdate', 1.5)}
        </>)}
        {ppRow(<>
          {ppLb('ชื่อ-สกุล')}
          {pi('st_name', 4)}
          {ppLb('เพศ')}
          {ps('st_gen', GENDER_OPT, 1)}
          {ppLb('หมู่โลหิต')}
          {pi('st_blood', 0.8)}
        </>)}
        {ppRow(<>
          {ppLb('เลขประจำตัวประชาชน')}
          {piIdCard(3)}
          {ppLb('สัญชาติ')}
          {pi('st_nationality', 1.2)}
          {ppLb('ศาสนา')}
          {pi('st_religion', 1.2)}
        </>)}
        {ppRow(<>
          {ppLb('เป็นบุตรคนที่')}
          {pi('st_childnumber', 0.6, 30)}
          {ppLb('จำนวนพี่น้องร่วมสายโลหิต')}
          {pi('st_sibling', 0.6, 30)}
          {ppLb('คน  ชาย')}
          {pi('st_malesibling', 0.5, 30)}
          {ppLb('คน  หญิง')}
          {pi('st_femalesibling', 0.5, 30)}
          {ppLb('คน')}
        </>)}
        {addrRow('ภูมิลำเนาเดิม',  'st_oldaddress', 'st_oldSubDistrict',     'st_oldDistrict',     'st_oldProvince')}
        {addrRow('ที่อยู่ปัจจุบัน', 'st_current',    'st_currentSubDistrict', 'st_currentDistrict', 'st_currentProvince')}

        {ppSec('ข้อมูลครอบครัว')}
        {ppRow(<>
          {ppLb('บิดาชื่อ')}
          {pi('st_fatherName', 3)}
          {ppLb('อาชีพ')}
          {pi('st_fatherJob', 2)}
          {ppLb('รายได้/ปี')}
          {pi('st_fatherAnnualIncome', 1.5)}
        </>)}
        {addrRow('ที่อยู่', 'st_fatherAddress', 'st_fatherSubDistrict', 'st_fatherDistrict', 'st_fatherProvince')}
        {ppRow(<>
          {ppLb('มารดาชื่อ')}
          {pi('st_motherName', 3)}
          {ppLb('อาชีพ')}
          {pi('st_motherJob', 2)}
          {ppLb('รายได้/ปี')}
          {pi('st_motherAnnualIncome', 1.5)}
        </>)}
        {addrRow('ที่อยู่', 'st_motherAddress', 'st_motherSubDistrict', 'st_motherDistrict', 'st_motherProvince')}
        {ppRow(<>
          {ppLb('รายได้ครอบครัวเฉลี่ย/ปี')}
          {pi('st_averageAnnualIncome', 2)}
          {ppLb('สภาพบิดามารดา')}
          {ps('st_parentStatus', PSTATUS_OPT, 2)}
        </>)}
        {ppRow(<>
          {ppLb('ผู้ปกครองชื่อ')}
          {pi('st_parentname', 3)}
          {ppLb('อาชีพ')}
          {pi('st_parentjob', 2)}
          {ppLb('รายได้/ปี')}
          {pi('st_parentannualincome', 1.5)}
        </>)}
        {addrRow('ที่อยู่', 'st_parentAddress', 'st_parentSubDistrict', 'st_parentDistrict', 'st_parentProvince')}
        {ppRow(<>
          {ppLb('ผู้ออกค่าใช้จ่ายในการเรียนให้')}
          {pi('st_educationsupporter')}
        </>)}

        {ppSec('ความสนใจและแนวทาง')}
        {ppRow(<>{ppLb('กีฬาที่สนใจ')}{pi('st_favoritesport')}</>)}
        {ppRow(<>{ppLb('วิชาที่ชอบ')}{pi('st_favoritesubject')}</>)}
        {ppRow(<>{ppLb('แนวทางการศึกษาต่อ')}{pi('st_futurestudy')}</>)}
        {ppRow(<>{ppLb('แนวทางการประกอบอาชีพในอนาคต')}{pi('st_futurejob')}</>)}

        <div className="no-print" style={{ textAlign: 'right', marginTop: 8 }}>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกข้อมูลส่วนตัว</>}
          </Button>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant="primary" className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i>{' ปริ้นข้อมูลส่วนตัว'}
        </Button>
      </div>
    </div>
  );
}

// Editable height/weight table + medical text sections for one A4 health page
function HealthSectionPage({ student, years, levelLabel, showMedical }) {
  const ref = useRef();

  // bm[year_id][month] = { height, weight }
  const [bm, setBm] = useState({});
  const [savingYears, setSavingYears] = useState({});

  const [med, setMed] = useState({ st_everSyndrome: '', st_food: '', st_drug: '', st_syndrome: '', st_facingSyndrome: '', st_accidentHistory: '' });
  const [savingMed, setSavingMed] = useState(false);

  useEffect(() => {
    if (student) setMed({
      st_everSyndrome:   student.st_everSyndrome   || '',
      st_food:           student.st_food           || '',
      st_drug:           student.st_drug           || '',
      st_syndrome:       student.st_syndrome       || '',
      st_facingSyndrome: student.st_facingSyndrome || '',
      st_accidentHistory:student.st_accidentHistory|| '',
    });
  }, [student]);

  useEffect(() => {
    if (!years.length) return;
    years.forEach(yr => {
      axiosInstance.get(
        `${process.env.REACT_APP_API}/getBodyMass/${student.st_id}/${yr.year_id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      ).then(res => {
        setBm(prev => {
          const next = { ...prev, [yr.year_id]: {} };
          res.data.forEach(r => {
            next[yr.year_id][r.stmb_month] = {
              height: r.stbm_height ?? '',
              weight: r.stbm_weight ?? '',
            };
          });
          return next;
        });
      }).catch(() => {});
    });
  }, [student.st_id, years]); // eslint-disable-line

  const handleChange = (yearId, month, field, value) => {
    setBm(prev => ({
      ...prev,
      [yearId]: {
        ...prev[yearId],
        [month]: { ...prev[yearId]?.[month], [field]: value },
      },
    }));
  };

  const handleSaveYear = (yr) => {
    const ymBm = bm[yr.year_id] || {};
    const records = HP_MONTHS.map(m => ({
      month: m.num,
      height: ymBm[m.num]?.height !== '' && ymBm[m.num]?.height != null ? ymBm[m.num].height : null,
      weight: ymBm[m.num]?.weight !== '' && ymBm[m.num]?.weight != null ? ymBm[m.num].weight : null,
    }));
    setSavingYears(prev => ({ ...prev, [yr.year_id]: true }));
    axiosInstance.post(
      `${process.env.REACT_APP_API}/saveBodyMassYear`,
      { studentId: student.st_id, yearId: yr.year_id, records, update_by: getUserId() },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' });
    }).finally(() => {
      setSavingYears(prev => ({ ...prev, [yr.year_id]: false }));
    });
  };

  const inputStyle = {
    width: '100%', border: 'none', outline: 'none',
    textAlign: 'center', background: 'transparent', fontSize: '10pt',
  };

  const hpInput = (field) => (
    <div style={{ borderBottom: '1px solid #000', minHeight: 26, marginBottom: 8, paddingBottom: 2 }}>
      <span className="no-print">
        <input type="text" value={med[field]} onChange={e => setMed(f => ({ ...f, [field]: e.target.value }))}
          style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: 'inherit', padding: '0 6px' }} />
      </span>
      <span className="print-only" style={{ paddingLeft: med[field] ? '2em' : 0, display: 'block', minHeight: 20 }}>{med[field] || ' '}</span>
    </div>
  );

  const handleSaveMedical = () => {
    setSavingMed(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/updateStudentInfo`,
      { ...med, st_id: student.st_id, st_idnumber: student.st_idnumber, st_name: student.st_name, st_status: student.st_status },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false }))
     .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' }))
     .finally(() => setSavingMed(false));
  };

  const btnLabel = levelLabel ? `ปริ้นด้านสุขภาพ (${levelLabel})` : 'ปริ้นด้านสุขภาพ';

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '12.5pt', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 'bold', fontSize: '15pt', marginBottom: 6 }}>
          {'2. ด้านสุขภาพ'}
          {levelLabel
            ? <span style={{ fontWeight: 'normal', fontSize: '13pt' }}>{` (${levelLabel})`}</span>
            : null}
        </div>

        {ppSec('2.1 ส่วนสูงและน้ำหนัก')}

        {years.map(yr => {
          const ymBm = bm[yr.year_id] || {};
          const isSaving = savingYears[yr.year_id] || false;
          return (
            <div key={yr.year_id}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 2, fontSize: '11pt' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'left' }}>
                      {'ปีการศึกษา'}&emsp;{yr.year_year}
                    </th>
                    {HP_MONTHS.map(m => (
                      <th key={m.num} style={{ border: '1px solid #000', textAlign: 'center', padding: '4px 8px', width: '15%' }}>
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ height: 32 }}>
                    <td style={{ border: '1px solid #000', padding: '2px 8px' }}>{'ส่วนสูง (เซนติเมตร)'}</td>
                    {HP_MONTHS.map(m => (
                      <td key={m.num} style={{ border: '1px solid #000', textAlign: 'center', padding: '1px 2px' }}>
                        <span className="no-print">
                          <input type="number" value={ymBm[m.num]?.height ?? ''}
                            onChange={e => handleChange(yr.year_id, m.num, 'height', e.target.value)}
                            style={inputStyle} />
                        </span>
                        <span className="print-only">{ymBm[m.num]?.height ?? ''}</span>
                      </td>
                    ))}
                  </tr>
                  <tr style={{ height: 32 }}>
                    <td style={{ border: '1px solid #000', padding: '2px 8px' }}>{'น้ำหนัก (กิโลกรัม)'}</td>
                    {HP_MONTHS.map(m => (
                      <td key={m.num} style={{ border: '1px solid #000', textAlign: 'center', padding: '1px 2px' }}>
                        <span className="no-print">
                          <input type="number" value={ymBm[m.num]?.weight ?? ''}
                            onChange={e => handleChange(yr.year_id, m.num, 'weight', e.target.value)}
                            style={inputStyle} />
                        </span>
                        <span className="print-only">{ymBm[m.num]?.weight ?? ''}</span>
                      </td>
                    ))}
                  </tr>
                  <tr style={{ height: 32 }}>
                    <td style={{ border: '1px solid #000', padding: '2px 8px' }}>{'การประเมินค่าดัชนีมวลกาย'}</td>
                    {HP_MONTHS.map(m => {
                      const bmiVal = calcBMI(ymBm[m.num]?.height, ymBm[m.num]?.weight);
                      return (
                        <td key={m.num} style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt', padding: '2px 4px' }}>
                          {bmiText(bmiVal)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
              <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button variant="success" size="sm" onClick={() => handleSaveYear(yr)} disabled={isSaving}>
                  {isSaving
                    ? <Spinner animation="border" size="sm" />
                    : <><i className="ri-save-line me-1"></i>บันทึกส่วนสูง/น้ำหนัก {yr.year_year}</>}
                </Button>
              </div>
            </div>
          );
        })}

        {showMedical && <>
          {ppSec('2.2 โรคที่เคยเป็น')}
          {hpInput('st_everSyndrome')}
          <div style={{ borderBottom: '1px solid #000', minHeight: 26, marginBottom: 8 }} />

          {ppSec('2.3 การแพ้พิษ – แพ้ยา')}
          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div className="no-print" style={{ fontSize: '11pt', color: '#555', marginBottom: 2 }}>แพ้พิษ / อาหาร</div>
              {hpInput('st_food')}
            </div>
            <div style={{ flex: 1 }}>
              <div className="no-print" style={{ fontSize: '11pt', color: '#555', marginBottom: 2 }}>แพ้ยา</div>
              {hpInput('st_drug')}
            </div>
          </div>

          {ppSec('2.4 โรคประจำตัว')}
          {hpInput('st_syndrome')}
          <div style={{ borderBottom: '1px solid #000', minHeight: 26, marginBottom: 8 }} />

          {ppSec('2.5 วิธีแก้ไขเมื่อเกิดการเจ็บป่วยด้วยโรคประจำตัว')}
          {hpInput('st_facingSyndrome')}
          <div style={{ borderBottom: '1px solid #000', minHeight: 26, marginBottom: 8 }} />

          {ppSec('2.6 อุบัติเหตุร้ายแรงและความผิดปกติอื่นๆ')}
          {hpInput('st_accidentHistory')}
          <div style={{ borderBottom: '1px solid #000', minHeight: 26, marginBottom: 8 }} />

          <div className="no-print" style={{ textAlign: 'right', marginTop: 4 }}>
            <Button variant="success" size="sm" onClick={handleSaveMedical} disabled={savingMed}>
              {savingMed ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกข้อมูลสุขภาพ</>}
            </Button>
          </div>
        </>}
      </div>
      <div className="print-button-container no-print">
        <Button variant="secondary" className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i>{` ${btnLabel}`}
        </Button>
      </div>
    </div>
  );
}

function HealthPagePP8({ student, studyHistory }) {
  const juniorYears = filterYears(studyHistory, true);
  const seniorYears = filterYears(studyHistory, false);
  const hasBoth     = juniorYears.length > 0 && seniorYears.length > 0;

  if (hasBoth) {
    return (
      <>
        <HealthSectionPage student={student} years={juniorYears} levelLabel="ม.ต้น"  showMedical={true} />
        <HealthSectionPage student={student} years={seniorYears} levelLabel="ม.ปลาย" showMedical={true} />
      </>
    );
  }

  const years = juniorYears.length > 0 ? juniorYears : seniorYears;
  return <HealthSectionPage student={student} years={years} levelLabel="" showMedical={true} />;
}

const ROWS = 10;
const emptySchRows = () => Array.from({ length: ROWS }, (_, i) => ({ order: i + 1, name: '', type: '', provider: '' }));
const emptyProjRows = () => Array.from({ length: ROWS }, (_, i) => ({ order: i + 1, name: '', reason: '', cls: '' }));

function ScholarshipPagePP8({ student }) {
  const ref = useRef();

  const [scholarships, setScholarships] = useState(emptySchRows);
  const [projects,     setProjects]     = useState(emptyProjRows);
  const [savingSch,    setSavingSch]    = useState(false);
  const [savingProj,   setSavingProj]   = useState(false);

  useEffect(() => {
    if (!student?.st_id) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getScholarship/${student.st_id}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(res => {
      if (res.data.length > 0) {
        const filled = emptySchRows();
        res.data.forEach((r, i) => {
          if (i < ROWS) filled[i] = { order: i + 1, name: r.sch_name || '', type: r.sch_type || '', provider: r.sch_provider || '' };
        });
        setScholarships(filled);
      }
    }).catch(() => {});

    axiosInstance.get(`${process.env.REACT_APP_API}/getProject/${student.st_id}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(res => {
      if (res.data.length > 0) {
        const filled = emptyProjRows();
        res.data.forEach((r, i) => {
          if (i < ROWS) filled[i] = { order: i + 1, name: r.proj_name || '', reason: r.proj_reason || '', cls: r.proj_class || '' };
        });
        setProjects(filled);
      }
    }).catch(() => {});
  }, [student?.st_id]); // eslint-disable-line

  const handleSaveScholarship = () => {
    setSavingSch(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/saveScholarship`,
      { st_id: student.st_id, rows: scholarships, update_by: getUserId() },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false }))
     .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' }))
     .finally(() => setSavingSch(false));
  };

  const handleSaveProject = () => {
    setSavingProj(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/saveProject`,
      { st_id: student.st_id, rows: projects, update_by: getUserId() },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false }))
     .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' }))
     .finally(() => setSavingProj(false));
  };

  const cellInput = (value, onChange) => (
    <>
      <span className="no-print">
        <input type="text" value={value} onChange={onChange}
          style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '11pt', padding: '1px 3px' }} />
      </span>
      <span className="print-only">{value}</span>
    </>
  );

  const tdStyle = { border: '1px solid #000', padding: '1px 2px' };

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '13pt' }}>
        <div style={{ fontWeight: 'bold', fontSize: '15pt', marginBottom: 10 }}>
          {'3. ทุนการศึกษาหรือรางวัลการศึกษา'}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: '12pt' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '40%' }}>{'ทุนหรือรางวัลการศึกษา'}</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '35%' }}>{'ประเภทหรือรางวัล'}</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '25%' }}>{'ผู้ให้ทุนหรือรางวัล'}</th>
            </tr>
          </thead>
          <tbody>
            {scholarships.map((row, i) => (
              <tr key={i} style={{ height: 30 }}>
                <td style={tdStyle}>{cellInput(row.name, e => setScholarships(prev => { const n = [...prev]; n[i] = { ...n[i], name: e.target.value }; return n; }))}</td>
                <td style={tdStyle}>{cellInput(row.type, e => setScholarships(prev => { const n = [...prev]; n[i] = { ...n[i], type: e.target.value }; return n; }))}</td>
                <td style={tdStyle}>{cellInput(row.provider, e => setScholarships(prev => { const n = [...prev]; n[i] = { ...n[i], provider: e.target.value }; return n; }))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="no-print" style={{ textAlign: 'right', marginBottom: 24 }}>
          <Button variant="success" size="sm" onClick={handleSaveScholarship} disabled={savingSch}>
            {savingSch ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกทุนการศึกษา</>}
          </Button>
        </div>

        <div style={{ fontWeight: 'bold', fontSize: '15pt', marginBottom: 10 }}>
          {'4. ผลงานที่ประทับใจหรือโครงงาน'}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: '12pt' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '30%' }}>{'ชื่อผลงานหรือโครงงาน'}</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '50%' }}>{'เหตุผลที่ประทับใจ'}</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '5px 8px', width: '20%' }}>{'ทำขึ้นในชั้น'}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((row, i) => (
              <tr key={i} style={{ height: 30 }}>
                <td style={tdStyle}>{cellInput(row.name,   e => setProjects(prev => { const n = [...prev]; n[i] = { ...n[i], name:   e.target.value }; return n; }))}</td>
                <td style={tdStyle}>{cellInput(row.reason, e => setProjects(prev => { const n = [...prev]; n[i] = { ...n[i], reason: e.target.value }; return n; }))}</td>
                <td style={tdStyle}>{cellInput(row.cls,    e => setProjects(prev => { const n = [...prev]; n[i] = { ...n[i], cls:    e.target.value }; return n; }))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="no-print" style={{ textAlign: 'right' }}>
          <Button variant="success" size="sm" onClick={handleSaveProject} disabled={savingProj}>
            {savingProj ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกผลงาน</>}
          </Button>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant="secondary" className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i>{' ปริ้นทุนการศึกษาและผลงาน'}
        </Button>
      </div>
    </div>
  );
}

function CoverPagesPP8({ student, studyHistory, school }) {
  const juniorHistory = studyHistory.filter(r => Number(r.c_id) <= 3);
  const seniorHistory = studyHistory.filter(r => Number(r.c_id) > 3);
  return (
    <>
      {juniorHistory.length > 0 && (
        <CoverPagePP8 student={student} studyHistory={juniorHistory} school={school} />
      )}
      {seniorHistory.length > 0 && (
        <CoverPagePP8 student={student} studyHistory={seniorHistory} school={school} />
      )}
    </>
  );
}

export default function StudentPP8() {
  const { studentID }  = useParams();
  const { setLoading } = useLoading();
  const schoolSettings = useSchoolSettings();

  const [studentData, setStudentData]   = useState(null);
  const [studyHistory, setStudyHistory] = useState([]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudentInfo/${studentID}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => setStudentData(res.data))
    .catch(err => console.error(err))
    .finally(() => setLoading(false));
  }, [studentID, setLoading]);

  useEffect(() => {
    if (!studentData?.st_id) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudentGradePP/${studentData.st_id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => setStudyHistory(res.data?.study_history || []))
    .catch(err => console.error(err));
  }, [studentData]);

  if (!studentData) return null;

  return (
    <>
      <Header />
      <div className="container mt-4">
        <div className="a4-wrapper">
          <div>
            <CoverPagesPP8     student={studentData} studyHistory={studyHistory} school={schoolSettings} />
            <PersonalPagePP8   student={studentData} onSaved={setStudentData} />
            <HealthPagePP8     student={studentData} studyHistory={studyHistory} />
            <ScholarshipPagePP8 student={studentData} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
